// People Section Variants Configuration
// Vice Chancellor, Faculty, Testimonials, Masterclass, Placements
//
// MANDATORY when adding a variant here: give it a human-readable `name`. It is
// spread into SECTION_VARIANTS by ./index.ts and read through
// `getVariantDetails()` by the page builder and by **Widget Management**
// (`/admin/widget-management`), where that string is the only label identifying
// the widget before someone deletes it from live pages. Miss it and the screen
// falls back to the raw id.
//
// Full checklist for a new section or variant: top of ../sectionTypes.ts.

import {
  setViceChancellor1,
  setFaculty1,
  setFaculty2,
  setFaculty3,
  setFaculty4,
  setFaculty5,
  setFaculty6,
  setTestimonials1,
  setTestimonials2,
  setTestimonials1056,
  setMasterclass1,
  setPlacements1,
  setPlacements2,
  setPlacements3,
  setPlacements4,
} from "@/store/slices/page";
import { Form_1, Form_8 } from "@/components/organisms/admin/forms";
import type { PageState } from "@/store/slices/page";
import { VICE_CHANCELLOR_1_CONSTANTS } from "../fields/viceChancellor/viceChancellor_1";
import { FACULTY_1_CONSTANTS } from "../fields/faculty/faculty_1";
import { FACULTY_2_CONSTANTS } from "../fields/faculty/faculty_2";
import { FACULTY_3_CONSTANTS } from "../fields/faculty/faculty_3";
import { FACULTY_4_CONSTANTS } from "../fields/faculty/faculty_4";
import { FACULTY_5_CONSTANTS } from "../fields/faculty/faculty_5";
import { FACULTY_6_CONSTANTS } from "../fields/faculty/faculty_6";
import { TESTIMONIALS_1_CONSTANTS } from "../fields/testimonials/testimonials_1";
import { TESTIMONIALS_2_CONSTANTS } from "../fields/testimonials/testimonials_2";
import { TESTIMONIALS_1056_CONSTANTS } from "../fields/testimonials/testimonials_1056";
import { MASTERCLASS_1_CONSTANTS } from "../fields/masterclass/masterclass_1";
import { PLACEMENTS_1_CONSTANTS } from "../fields/placements/placements_1";
import { PLACEMENTS_2_CONSTANTS } from "../fields/placements/placements_2";
import { PLACEMENTS_3_CONSTANTS } from "../fields/placements/placements_3";
import { PLACEMENTS_4_CONSTANTS } from "../fields/placements/placements_4";
import {
  createViceChancellor,
  updateViceChancellor,
} from "@/api/admin/viceChancellor";
import { createFaculty, updateFaculty } from "@/api/admin/faculty";
import {
  createTestimonials,
  updateTestimonials,
  normalizeTestimonialsScrollFrequencyForDb,
} from "@/api/admin/testimonials";
import { createMasterclass, updateMasterclass } from "@/api/admin/masterclass";
import { createPlacements, updatePlacements } from "@/api/admin/placements";
import About1 from "@/widgets/about/About1";
import Faculty1 from "@/widgets/faculty/Faculty1";
import Faculty2 from "@/widgets/faculty/Faculty2";
import Faculty3 from "@/widgets/faculty/Faculty3";
import Faculty4 from "@/widgets/faculty/Faculty4";
import Faculty5 from "@/widgets/faculty/Faculty5";
import Faculty6 from "@/widgets/faculty/Faculty6";
import Testimonials1 from "@/widgets/studenttestimonials/Testimonials1";
import Testimonials2 from "@/widgets/studenttestimonials/Testimonials2";
//import Testimonials2 from "@/widgets/studenttestimonials/Testimonials2";
import Testimonials1056 from "@/widgets/studenttestimonials/Testimonials1056";
import OnlineMasterclass1 from "@/widgets/onlinemasterclass/OnlineMasterclass1";
import Placements from "@/widgets/placements/Placements";
import { Placements2 } from "@/widgets/placements/Placements2";
import { Placements3 } from "@/widgets/placements/Placements3";
import { Placements4 } from "@/widgets/placements/Placements4";

import {
  SECTION_TYPES,
  VICE_CHANCELLOR_VARIANTS,
  FACULTY_VARIANTS,
  TESTIMONIALS_VARIANTS,
  MASTERCLASS_VARIANTS,
  PLACEMENTS_VARIANTS,
} from "../sectionTypes";
import { extractMetadataFields } from "@/helpers";
import { getPreviewData, activateMetadata } from "@/utils/variantHelpers";

export const PEOPLE_SECTION_VARIANTS = {
  // [SECTION_TYPES.VICE_CHANCELLOR]: [
  //   {
  //     id: VICE_CHANCELLOR_VARIANTS.VICE_CHANCELLOR_1,
  //     name: "Vice Chancellor 1",
  //     description: "Vice Chancellor messages",
  //     componentFields:
  //       VICE_CHANCELLOR_1_CONSTANTS.VICE_CHANCELLOR_FIELDS_CONFIG,
  //     ...extractMetadataFields(VICE_CHANCELLOR_1_CONSTANTS),
  //     showTable: false,
  //     setAction: setViceChancellor1,
  //     formComponent: Form_1,
  //     previewComponent: About1,
  //     selector: (state: PageState) => state.vice_chancellor_1,
  //     createMethod: createViceChancellor,
  //     updateMethod: updateViceChancellor,
  //     preparePreviewData: ({ section, reduxState, isSelected }: any) => {
  //       const data = getPreviewData(section, reduxState, isSelected);
  //       return {
  //         data: {
  //           data: {
  //             ...data,
  //             metadata: activateMetadata(data.metadata),
  //             status: true,
  //           },
  //         },
  //       };
  //     },
  //   },
  // ],
  [SECTION_TYPES.FACULTY]: [
    {
      id: FACULTY_VARIANTS.FACULTY_1,
      name: "Faculty 1",
      description: "Faculty information",
      componentFields: FACULTY_1_CONSTANTS.FACULTY_FIELDS_CONFIG,
      ...extractMetadataFields(FACULTY_1_CONSTANTS),
      showTable: false,
      setAction: setFaculty1,
      formComponent: Form_1,
      previewComponent: Faculty1,
      selector: (state: PageState) => state.faculty_1,
      createMethod: createFaculty,
      updateMethod: updateFaculty,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              metadata: activateMetadata(data.metadata),
              status: true,
            },
          },
        };
      },
    },
    {
      id: FACULTY_VARIANTS.FACULTY_2,
      name: "Faculty 2",
      description: "Faculty with gradient backgrounds and enhanced cards",
      componentFields: FACULTY_2_CONSTANTS.FACULTY_FIELDS_CONFIG,
      ...extractMetadataFields(FACULTY_2_CONSTANTS),
      showTable: true,
      setAction: setFaculty2,
      formComponent: Form_1,
      previewComponent: Faculty2,
      selector: (state: PageState) => state.faculty_2,
      createMethod: createFaculty,
      updateMethod: updateFaculty,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              title_1: data.title_1 || "Meet Our",
              title_1_color: data.title_1_color || "rgba(255, 255, 255, 0.8)",
              title_2: data.title_2 || "Faculty",
              title_2_gradient_start_color: data.title_2_gradient_start_color || "#FFFFFF",
              title_2_gradient_end_color: data.title_2_gradient_end_color || "#02A7B6",
              title_2_gradient_angle: data.title_2_gradient_angle || "90deg",
              left_navigation_icon: data.left_navigation_icon || "",
              left_navigation_icon_alt: data.left_navigation_icon_alt || "Left navigation",
              right_navigation_icon: data.right_navigation_icon || "",
              right_navigation_icon_alt: data.right_navigation_icon_alt || "Right navigation",
              range_color: data.range_color || "#00D4EB",
              cta_button_color: data.cta_button_color || "#02A7B6",
              cta_text_color: data.cta_text_color || "#FFFFFF",
              cta_icon: data.cta_icon || "",
              cta_text: data.cta_text || "Download Brochure",
              cta_icon_alt: data.cta_icon_alt || "CTA icon",
              auto_scroll_frequency: data.auto_scroll_frequency || 5000,
              backvariant: data.backvariant || "variant_1",
              // The preview builds an explicit whitelist, so layout_variant has
              // to be forwarded or variant 2 always previews as variant 1.
              layout_variant: data.layout_variant || "variant_1",
              gradient_color_1: data.gradient_color_1 || "#00D4EB",
              gradient_color_2: data.gradient_color_2 || "#000000",
              gradient_color_3: data.gradient_color_3 || "#000000",
              gradient_color_4: data.gradient_color_4 || "#5345C3",
              background_color: data.background_color || "#000000",
              metadata: activateMetadata(data.metadata),
              status: true,
            },
          },
        };
      },
    },
    {
      id: FACULTY_VARIANTS.FACULTY_3,
      name: "Faculty 3",
      description:
        "Horizontal scroll faculty cards — 276×156 image, name, designation, institute logo",
      componentFields: FACULTY_3_CONSTANTS.FACULTY_FIELDS_CONFIG,
      ...extractMetadataFields(FACULTY_3_CONSTANTS),
      showTable: true,
      setAction: setFaculty3,
      formComponent: Form_1,
      previewComponent: Faculty3,
      selector: (state: PageState) => state.faculty_3,
      createMethod: createFaculty,
      updateMethod: updateFaculty,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              title: data.title ?? "Learning Led by ",
              title_text_color: data.title_text_color ?? "#000000",
              title_1: data.title_1 ?? "Distinguished Faculty",
              title_1_color: data.title_1_color ?? "#E36E26",
              description:
                data.description ??
                "India's Strongest Academic Bench in AI & Data Science",
              description_text_color: data.description_text_color ?? "#000000",
              description_background_color:
                data.description_background_color ?? "#FFFFFF",
              metadata: activateMetadata(data.metadata),
              status: true,
            },
          },
        };
      },
    },
    {
      id: FACULTY_VARIANTS.FACULTY_4,
      name: "Faculty 4 — Carousel slides",
      description:
        "Carousel of faculty slides: shared title + per-slide image / name / bio. Prev/next arrows; all colors admin-driven. Reuses existing faculty columns — no migration needed.",
      componentFields: FACULTY_4_CONSTANTS.FACULTY_FIELDS_CONFIG,
      ...extractMetadataFields(FACULTY_4_CONSTANTS),
      showTable: false,
      setAction: setFaculty4,
      formComponent: Form_1,
      previewComponent: Faculty4,
      selector: (state: PageState) => state.faculty_4,
      createMethod: createFaculty,
      updateMethod: updateFaculty,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              status: data.status !== false,
              title: data.title ?? "Learning Led by {Distinguished Faculty}",
              title_text_color: data.title_text_color ?? "#FFFFFF",
              title_1_color: data.title_1_color ?? "#FFD700",
              background_color: data.background_color ?? "#291973",
              cta_button_color: data.cta_button_color ?? "#E36E26",
              cta_text_color: data.cta_text_color ?? "#FFFFFF",
              metadata: activateMetadata(data.metadata),
            },
          },
        };
      },
    },
    {
      id: FACULTY_VARIANTS.FACULTY_5,
      name: "Faculty 5 — Leadership cards",
      description:
        "Leadership-style section: optional full-bleed background image behind a centered title + description, with a row of leader cards (photo, name, bio, up to 3 company logos). Adds background_image columns.",
      componentFields: FACULTY_5_CONSTANTS.FACULTY_FIELDS_CONFIG,
      ...extractMetadataFields(FACULTY_5_CONSTANTS),
      showTable: false,
      setAction: setFaculty5,
      formComponent: Form_1,
      previewComponent: Faculty5,
      selector: (state: PageState) => state.faculty_5,
      createMethod: createFaculty,
      updateMethod: updateFaculty,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              status: data.status !== false,
              title: data.title ?? "Meet Our {Leadership}",
              title_text_color: data.title_text_color ?? "#1A1A1A",
              title_1_color: data.title_1_color ?? "#E36E26",
              description: data.description ?? "",
              description_text_color: data.description_text_color ?? "#1A1A1A",
              background_color: data.background_color ?? "#FFFFFF",
              background_image: data.background_image ?? "",
              background_image_alt: data.background_image_alt ?? "",
              metadata: activateMetadata(data.metadata),
            },
          },
        };
      },
    },
    {
      id: FACULTY_VARIANTS.FACULTY_6,
      name: "Faculty 6 — Testimonial carousel",
      description:
        "Testimonial-style carousel: centered title + description above one-card-per-view cards (circular photo with optional video play button, quote, name, role). Reuses existing faculty columns — no new columns, only an ENUM widen.",
      componentFields: FACULTY_6_CONSTANTS.FACULTY_FIELDS_CONFIG,
      ...extractMetadataFields(FACULTY_6_CONSTANTS),
      showTable: false,
      setAction: setFaculty6,
      formComponent: Form_1,
      previewComponent: Faculty6,
      selector: (state: PageState) => state.faculty_6,
      createMethod: createFaculty,
      updateMethod: updateFaculty,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              status: data.status !== false,
              title: data.title ?? "What Our {Students Say}",
              title_text_color: data.title_text_color ?? "#1A1A1A",
              title_1_color: data.title_1_color ?? "#E1473F",
              description: data.description ?? "",
              description_text_color: data.description_text_color ?? "#1A1A1A",
              background_color: data.background_color ?? "#FFFFFF",
              metadata: activateMetadata(data.metadata),
            },
          },
        };
      },
    },
  ],
  [SECTION_TYPES.TESTIMONIALS]: [
    {
      id: TESTIMONIALS_VARIANTS.TESTIMONIALS_1,
      name: "Testimonials 1",
      description: "Student testimonials",
      componentFields: TESTIMONIALS_1_CONSTANTS.TESTIMONIALS_FIELDS_CONFIG,
      ...extractMetadataFields(TESTIMONIALS_1_CONSTANTS),
      showTable: false,
      setAction: setTestimonials1,
      formComponent: Form_1,
      previewComponent: Testimonials1,
      selector: (state: PageState) => state.testimonials_1,
      createMethod: createTestimonials,
      updateMethod: updateTestimonials,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              metadata: activateMetadata(data.metadata),
              status: true,
            },
          },
        };
      },
    },
    {
      id: TESTIMONIALS_VARIANTS.TESTIMONIALS_1056,
      name: "Testimonials 1056 — KUK Home student testimonials",
      description:
        "KUK home student testimonials — a horizontal rail of portrait quote cards that dims every card not (almost) fully in view, with prev / next buttons and position dots below 768px",
      componentFields: TESTIMONIALS_1056_CONSTANTS.TESTIMONIALS_FIELDS_CONFIG,
      ...extractMetadataFields(TESTIMONIALS_1056_CONSTANTS),
      showTable: true,
      setAction: setTestimonials1056,
      formComponent: Form_1,
      previewComponent: Testimonials1056,
      selector: (state: PageState) => state.testimonials_1056,
      createMethod: createTestimonials,
      updateMethod: updateTestimonials,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              // Not activateMetadata: the widget drops status-false rows itself,
              // so the preview must see the same rows the live page does.
              metadata: Array.isArray(data.metadata) ? data.metadata : [],
              status: true,
            },
          },
        };
      },
    },
    {
      id: TESTIMONIALS_VARIANTS.TESTIMONIALS_2,
      name: "Testimonials 2 (Video Section)",
      description: "Testimonials with gradient background, quote icon, video option, arrows, configurable scroll",
      componentFields: TESTIMONIALS_2_CONSTANTS.TESTIMONIALS_FIELDS_CONFIG,
      ...extractMetadataFields(TESTIMONIALS_2_CONSTANTS),
      showTable: true,
      setAction: setTestimonials2,
      formComponent: Form_1,
      previewComponent: Testimonials2,
      selector: (state: PageState) => state.testimonials_2,
      createMethod: createTestimonials,
      updateMethod: updateTestimonials,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              desktop_background_color: data.desktop_background_color || "#FFFFFF",
              mobile_background_color: data.mobile_background_color || "#FFFFFF",
              backvariant: data.backvariant || "variant_1",
              gradient_color_1: data.gradient_color_1 || "rgba(0, 212, 235, 0.33)",
              gradient_color_2: data.gradient_color_2 || "rgba(0, 0, 0, 0.66)",
              gradient_color_3: data.gradient_color_3 || "rgba(0, 0, 0, 0.66)",
              gradient_color_4: data.gradient_color_4 || "rgba(83, 69, 195, 0.46)",
              pre_title: data.pre_title || "",
              pre_title_color: data.pre_title_color || "#FFFFFF",
              title: data.title || "",
              title_start_color: data.title_start_color || "#ca6363",
              title_end_color: data.title_end_color || "",
              title_gradient_angle: data.title_gradient_angle || "90deg",
              scroll_frequency:
                normalizeTestimonialsScrollFrequencyForDb(
                  data.scroll_frequency
                ),
              left_navigation_icon: data.left_navigation_icon || "",
              left_navigation_icon_alt: data.left_navigation_icon_alt || "Previous",
              right_navigation_icon: data.right_navigation_icon || "",
              right_navigation_icon_alt: data.right_navigation_icon_alt || "Next",
              button_text: data.button_text || "Apply Now",
              button_text_color: data.button_text_color || "#222222",
              button_color: data.button_color || "#FFFFFF",
              enable_animation: Boolean(
                data.enable_animation === true || data.enable_animation === 1
              ),
              metadata: activateMetadata(data.metadata),
              status: true,
            },
          },
        };
      },
    },
  ],
  [SECTION_TYPES.MASTERCLASS]: [
    {
      id: MASTERCLASS_VARIANTS.MASTERCLASS_1,
      name: "Masterclass 1",
      description: "Masterclass information",
      componentFields: MASTERCLASS_1_CONSTANTS.MASTERCLASS_FIELDS_CONFIG,
      ...extractMetadataFields(MASTERCLASS_1_CONSTANTS),
      showTable: false,
      setAction: setMasterclass1,
      formComponent: Form_1,
      previewComponent: OnlineMasterclass1,
      selector: (state: PageState) => state.masterclass_1,
      createMethod: createMasterclass,
      updateMethod: updateMasterclass,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              metadata: activateMetadata(data.metadata),
              status: true,
            },
          },
        };
      },
    },
  ],
  [SECTION_TYPES.PLACEMENTS]: [
    {
      id: PLACEMENTS_VARIANTS.PLACEMENTS_1,
      name: "Placements 1",
      description: "Placement information",
      componentFields: PLACEMENTS_1_CONSTANTS.PLACEMENTS_FIELDS_CONFIG,
      ...extractMetadataFields(PLACEMENTS_1_CONSTANTS),
      showTable: false,
      setAction: setPlacements1,
      formComponent: Form_1,
      previewComponent: Placements,
      selector: (state: PageState) => state.placements_1,
      createMethod: createPlacements,
      updateMethod: updatePlacements,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              metadata: activateMetadata(data.metadata),
              status: true,
            },
          },
        };
      },
    },
    {
      id: PLACEMENTS_VARIANTS.PLACEMENTS_2,
      name: "Placements 2",
      description: "Placements 2",
      componentFields: PLACEMENTS_2_CONSTANTS.PLACEMENTS_FIELDS_CONFIG,
      ...extractMetadataFields(PLACEMENTS_2_CONSTANTS),
      showTable: true,
      setAction: setPlacements2,
      formComponent: Form_1,
      previewComponent: Placements2,
      selector: (state: PageState) => state.placements_2,
      createMethod: createPlacements,
      updateMethod: updatePlacements,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            desktop_background_color: data.desktop_background_color || "#FFFFFF",
            mobile_background_color: data.mobile_background_color || "#FFFFFF",
            backvariant: data.backvariant || "variant_1",
            gradient_color_1: data.gradient_color_1 || "rgba(0, 212, 235, 0.33)",
            gradient_color_2: data.gradient_color_2 || "rgba(0, 0, 0, 0.66)",
            gradient_color_3: data.gradient_color_3 || "rgba(0, 0, 0, 0.66)",
            gradient_color_4: data.gradient_color_4 || "rgba(83, 69, 195, 0.46)",
            title: data.title || "certifications alongside your MBA.",
            title_color: data.title_color || "#ca6363",
            button_text: data.button_text || "Apply Now",
            button_text_color: data.button_text_color || "#FFFFFF",
            button_color: data.button_color || "#222222",
            scroll_frequency: data.scroll_frequency || "3000",
            metadata: activateMetadata(data.metadata),
            status: true,
          },
        };
      },
    },
    {
      id: PLACEMENTS_VARIANTS.PLACEMENTS_3,
      name: "Placements 3",
      description: "Two rows with scrolling logo strip images; section background; independent frequency & direction per row. Use when image and background colors blend.",
      componentFields: PLACEMENTS_3_CONSTANTS.PLACEMENTS_FIELDS_CONFIG,
      ...extractMetadataFields(PLACEMENTS_3_CONSTANTS),
      showTable: false,
      setAction: setPlacements3,
      formComponent: Form_1,
      previewComponent: Placements3,
      selector: (state: PageState) => state.placements_3,
      createMethod: createPlacements,
      updateMethod: updatePlacements,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            ...data,
            metadata: activateMetadata(data.metadata),
            status: true,
          },
        };
      },
    },
    {
      id: PLACEMENTS_VARIANTS.PLACEMENTS_4,
      name: "Placements 4",
      description:
        "Configurable number of scrolling rows; each row has its own speed & direction and a list of individually-added icons that slide and expand on hover. Same side-blur fade as Placements 3.",
      // Section-level fields (background settings + status)
      componentFields: PLACEMENTS_4_CONSTANTS.PLACEMENTS_FIELDS_CONFIG,
      // Per-row config (scroll speed + direction) rendered inside each row
      categoryFieldsConfig: PLACEMENTS_4_CONSTANTS.ROW_FIELDS_CONFIG,
      // Icons added individually inside each row
      categoryMetadataFields: PLACEMENTS_4_CONSTANTS.ICON_FIELDS_CONFIG,
      // Generic-repeater configuration for the shared Form_8
      categoriesKey: "rows_config",
      itemsKey: "icons",
      showCategoryColors: false,
      repeaterSectionLabel: "Marquee Rows",
      repeaterSectionIcon: "🎞️",
      categorySingularLabel: "Row",
      categoryTitleLabel: "Row Label (optional)",
      categoryItemsLabel: "Icons / Logos",
      categoryItemsHint: "each icon slides in the row's direction",
      addCategoryLabel: "Add Row",
      showTable: false,
      setAction: setPlacements4,
      formComponent: Form_8,
      previewComponent: Placements4,
      selector: (state: PageState) => state.placements_4,
      createMethod: createPlacements,
      updateMethod: updatePlacements,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            ...data,
            rows_config: Array.isArray(data.rows_config)
              ? data.rows_config
              : [],
            status: true,
          },
        };
      },
    },
  ],
};
